/**
 * EnjoyOverlayCRUD — K2 · Admin Catalogue · Enjoy Overlay.
 *
 * Founder ruling 14 Jun 2026 night (K2 approved):
 *   "K2 = Enjoy Overlay CRUD + Media Upload under OS Admin. Re-shell
 *    of the existing surface, NOT a greenfield rebuild."
 *
 * Sibling of EnjoySignatureCRUD (K1) · same UX shape · K2-specific
 * fields (kind, season_category, date_note, price_from/currency,
 * direct_book_url, force_concierge). Reuses K1's ImageUploader.jsx
 * verbatim.
 *
 * Backend endpoints used:
 *   GET    /api/os-admin/catalogue/enjoy-overlay
 *   GET    /api/os-admin/catalogue/enjoy-overlay/{id}
 *   POST   /api/os-admin/catalogue/enjoy-overlay
 *   PATCH  /api/os-admin/catalogue/enjoy-overlay/{id}    (If-Match: "<version>")
 *   POST   /api/os-admin/catalogue/enjoy-overlay/{id}/publish
 *   POST   /api/os-admin/catalogue/enjoy-overlay/{id}/archive
 *   POST   /api/os-admin/catalogue/enjoy-overlay/{id}/restore
 *   POST   /api/os-admin/catalogue/enjoy-overlay/{id}/clone
 *   POST   /api/os-admin/catalogue/enjoy-overlay/{id}/image/upload
 *   DELETE /api/os-admin/catalogue/enjoy-overlay/{id}/image
 *   GET    /api/os-admin/catalogue/enjoy-overlay/{id}/audit
 *   DELETE /api/os-admin/catalogue/enjoy-overlay/{id}  (founder-only)
 */
import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import ImageUploader from "@/pages/admin/os/ImageUploader";

const ROOT = "/os-admin/catalogue/enjoy-overlay";

const STATUS_CHIP = {
    draft: { bg: "#fef3c7", fg: "#78350f", label: "Draft" },
    live: { bg: "#dcfce7", fg: "#14532d", label: "Live" },
    archived: { bg: "#e5e7eb", fg: "#374151", label: "Archived" },
};

const inputStyle = {
    width: "100%", padding: "8px 10px",
    border: "1px solid #d1d5db", borderRadius: 4,
    fontSize: 14, boxSizing: "border-box",
    background: "#fff", color: "#1a2332",
};

function StatusChip({ status }) {
    const c = STATUS_CHIP[status] || STATUS_CHIP.draft;
    return (
        <span
            data-testid={`k2-status-chip-${status}`}
            style={{
                background: c.bg, color: c.fg,
                padding: "2px 8px", borderRadius: 999,
                fontSize: 11, fontWeight: 600,
                letterSpacing: "0.05em", textTransform: "uppercase",
            }}
        >{c.label}</span>
    );
}

function Field({ label, testid, children }) {
    return (
        <label data-testid={`k2-field-${testid}`} style={{ display: "block", marginBottom: 12 }}>
            <span style={{ display: "block", fontSize: 12, color: "#6b7280", marginBottom: 4, fontWeight: 600 }}>
                {label}
            </span>
            {children}
        </label>
    );
}


function ListView({ items, total, q, onSearchChange, statusFilter, onStatusChange, onOpen, onCreate, loading, flagDisabled }) {
    return (
        <div data-testid="k2-list-view">
            <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
                <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }} data-testid="k2-title">
                    Enjoy Overlay · Catalogue
                </h1>
                <span style={{ color: "#666", fontSize: 13 }}>
                    {total} {total === 1 ? "row" : "rows"}
                </span>
                <div style={{ flex: 1 }} />
                <input
                    type="search"
                    placeholder="Search title / city / country / venue"
                    value={q}
                    onChange={(e) => onSearchChange(e.target.value)}
                    data-testid="k2-search"
                    style={{ ...inputStyle, width: 280 }}
                />
                <select
                    value={statusFilter || ""}
                    onChange={(e) => onStatusChange(e.target.value || null)}
                    data-testid="k2-status-filter"
                    style={{ ...inputStyle, width: 140 }}
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="live">Live</option>
                    <option value="archived">Archived</option>
                </select>
                <button
                    type="button"
                    onClick={onCreate}
                    data-testid="k2-create-btn"
                    style={{
                        background: "#111827", color: "white", border: 0,
                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                        fontWeight: 600, cursor: "pointer",
                    }}
                >+ New overlay</button>
            </div>
            {flagDisabled ? (
                <div data-testid="k2-flag-disabled-banner" style={{
                    padding: 12, marginBottom: 16, borderRadius: 4,
                    background: "#fef3c7", color: "#78350f",
                    fontSize: 13,
                }}>
                    K2 (Enjoy Overlay CRUD) is not enabled in this environment.
                    Set <code>CATALOGUE_K2_ENABLED=true</code> in
                    <code> /app/backend/.env</code> and restart the backend.
                </div>
            ) : null}
            {loading ? <div data-testid="k2-loading">Loading…</div> : null}
            <div data-testid="k2-grid" style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
                gap: 12,
            }}>
                {items.map(it => (
                    <button
                        key={it.id}
                        type="button"
                        onClick={() => onOpen(it.id)}
                        data-testid={`k2-row-${it.id}`}
                        style={{
                            textAlign: "left", padding: 14, border: "1px solid #e5e7eb",
                            borderRadius: 6, background: "white", color: "#1a2332", cursor: "pointer",
                            display: "flex", flexDirection: "column", gap: 8,
                        }}
                    >
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            <StatusChip status={it.publish_status} />
                            <span style={{ fontSize: 11, color: "#9ca3af" }}>v{it.version || 1}</span>
                        </div>
                        <div style={{ fontWeight: 600, fontSize: 15, lineHeight: 1.3 }}>
                            {it.title || <i>(untitled)</i>}
                        </div>
                        <div style={{ fontSize: 12, color: "#6b7280" }}>
                            {it.kind ? <>{it.kind} · </> : null}
                            {it.category}
                            {(it.city || it.country) ? <> · {[it.city, it.country].filter(Boolean).join(", ")}</> : null}
                        </div>
                        {it.image_url ? (
                            <img
                                src={it.image_url} alt=""
                                data-testid={`k2-row-${it.id}-thumb`}
                                style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 4 }}
                            />
                        ) : (
                            <div style={{
                                fontSize: 11, color: "#92400e",
                                background: "#fef3c7", padding: "4px 6px", borderRadius: 4,
                            }}>No image · cannot publish</div>
                        )}
                    </button>
                ))}
                {(!items.length && !loading) ? (
                    <div data-testid="k2-empty" style={{ color: "#9ca3af", padding: 24, textAlign: "center" }}>
                        No overlays match your filter.
                    </div>
                ) : null}
            </div>
        </div>
    );
}


function CreateForm({ onCreated, onCancel }) {
    const [draft, setDraft] = useState({
        title: "", category: "music", kind: "event",
        city: "", country: "", concierge_whisper: "",
    });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    const handle = (k, v) => setDraft(d => ({ ...d, [k]: v }));

    const submit = async () => {
        setBusy(true); setErr(null);
        try {
            const body = Object.fromEntries(
                Object.entries(draft).filter(([_, v]) => v && String(v).trim()),
            );
            const r = await api.post(ROOT, body);
            onCreated(r.data.id);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "validation_failed") {
                setErr("Validation: " + (detail.violations || []).join(" · "));
            } else {
                setErr(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid="k2-create-form" style={{ maxWidth: 560 }}>
            <h2 style={{ marginTop: 0 }}>New Enjoy overlay</h2>
            {err ? (
                <div data-testid="k2-create-error" style={{
                    padding: 8, background: "#fdecea", color: "#a02020",
                    borderRadius: 4, marginBottom: 8, fontSize: 13,
                }}>{err}</div>
            ) : null}
            <Field label="Title" testid="title">
                <input value={draft.title} onChange={e => handle("title", e.target.value)}
                       data-testid="k2-create-title" style={inputStyle} />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="Category" testid="category">
                    <input value={draft.category} onChange={e => handle("category", e.target.value)}
                           data-testid="k2-create-category" style={inputStyle} />
                </Field>
                <Field label="Kind" testid="kind">
                    <select value={draft.kind} onChange={e => handle("kind", e.target.value)}
                            data-testid="k2-create-kind" style={inputStyle}>
                        <option value="event">event</option>
                        <option value="experience">experience</option>
                    </select>
                </Field>
                <Field label="Country (ISO-2)" testid="country">
                    <input value={draft.country} onChange={e => handle("country", e.target.value.toUpperCase())}
                           maxLength={3} data-testid="k2-create-country" style={inputStyle} />
                </Field>
            </div>
            <Field label="City" testid="city">
                <input value={draft.city} onChange={e => handle("city", e.target.value)}
                       data-testid="k2-create-city" style={inputStyle} />
            </Field>
            <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                <textarea value={draft.concierge_whisper} maxLength={280}
                          onChange={e => handle("concierge_whisper", e.target.value)}
                          data-testid="k2-create-whisper"
                          style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
            </Field>
            <div style={{ display: "flex", gap: 8 }}>
                <button onClick={submit} disabled={busy || !draft.title}
                        data-testid="k2-create-submit"
                        style={{
                            background: "#111827", color: "white", border: 0,
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            fontWeight: 600, cursor: "pointer",
                            opacity: (busy || !draft.title) ? 0.5 : 1,
                        }}>
                    {busy ? "Creating…" : "Create draft"}
                </button>
                <button onClick={onCancel} data-testid="k2-create-cancel"
                        style={{
                            background: "white", color: "#374151",
                            border: "1px solid #d1d5db",
                            padding: "8px 14px", borderRadius: 4, fontSize: 13,
                            cursor: "pointer",
                        }}>Cancel</button>
            </div>
        </div>
    );
}


function DetailView({ rowId, onBack, onChanged }) {
    const [row, setRow] = useState(null);
    const [draft, setDraft] = useState({});
    const [dirty, setDirty] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [audit, setAudit] = useState([]);

    const refresh = useCallback(async () => {
        try {
            const r = await api.get(`${ROOT}/${rowId}`);
            setRow(r.data);
            setDraft({ ...r.data });
            setDirty(false);
            const a = await api.get(`${ROOT}/${rowId}/audit`);
            setAudit(a.data.items || []);
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail) || e.message);
        }
    }, [rowId]);

    useEffect(() => { refresh(); }, [refresh]);

    const handleField = (k, v) => {
        setDraft(d => ({ ...d, [k]: v }));
        setDirty(true);
    };

    const save = async () => {
        if (!row || !dirty) return;
        setBusy(true); setError(null);
        try {
            const patch = {};
            Object.entries(draft).forEach(([k, v]) => {
                if ((row[k] || "") !== (v || "")) patch[k] = v;
            });
            if (!Object.keys(patch).length) {
                setDirty(false); setBusy(false); return;
            }
            await api.patch(`${ROOT}/${rowId}`, patch, {
                headers: { "If-Match": `"${row.version}"` },
            });
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "version_conflict") {
                setError("Row was edited by another operator. Refresh and re-apply.");
            } else if (detail?.error === "validation_failed") {
                setError("Validation failed: " + (detail.violations || []).join(" · "));
            } else {
                setError(formatApiError(detail) || e.message);
            }
        } finally {
            setBusy(false);
        }
    };

    const callAction = async (action, label) => {
        if (!row) return;
        setBusy(true); setError(null);
        try {
            await api.post(`${ROOT}/${rowId}/${action}`);
            await refresh();
            onChanged?.();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (detail?.error === "publish_blocked") {
                const blocks = [
                    ...(detail.blocks || []),
                    ...(detail.gate_missing_fields || []).map(f => `required field missing: ${f}`),
                ];
                setError(`Publish blocked: ${blocks.join(" · ")}`);
            } else {
                setError(formatApiError(detail) || e.message || `${label} failed`);
            }
        } finally { setBusy(false); }
    };

    const handleImageUpload = async (file) => {
        const fd = new FormData();
        fd.append("file", file);
        await api.post(`${ROOT}/${rowId}/image/upload`, fd, {
            headers: { "Content-Type": "multipart/form-data" },
        });
        await refresh();
        onChanged?.();
    };

    const handleImageRemove = async () => {
        await api.delete(`${ROOT}/${rowId}/image`);
        await refresh();
        onChanged?.();
    };

    if (!row) {
        return (
            <div data-testid="k2-detail-loading">
                <button onClick={onBack} data-testid="k2-detail-back-btn" style={{ marginBottom: 16 }}>← Back to list</button>
                {error ? <div style={{ color: "#a02020" }}>{error}</div> : <div>Loading…</div>}
            </div>
        );
    }

    return (
        <div data-testid="k2-detail-view">
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <button onClick={onBack} data-testid="k2-detail-back-btn"
                        style={{ padding: "6px 10px", border: "1px solid #ccc", borderRadius: 4, background: "white", cursor: "pointer" }}>
                    ← Back to list
                </button>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, flex: 1 }} data-testid="k2-detail-title">
                    {row.title || <i>Untitled</i>}
                </h1>
                <StatusChip status={row.publish_status} />
                <span style={{ fontSize: 12, color: "#999" }}>v{row.version}</span>
            </div>

            {error ? (
                <div data-testid="k2-detail-error" style={{
                    padding: 10, marginBottom: 12, borderRadius: 4,
                    background: "#fdecea", color: "#a02020",
                }}>{error}</div>
            ) : null}

            <div style={{ display: "grid", gridTemplateColumns: "1fr 380px", gap: 24 }}>
                {/* LEFT · the form */}
                <div data-testid="k2-form">
                    <Field label="Title" testid="title">
                        <input value={draft.title || ""} onChange={e => handleField("title", e.target.value)}
                               data-testid="k2-input-title" style={inputStyle} />
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Category" testid="category">
                            <input value={draft.category || ""} onChange={e => handleField("category", e.target.value)}
                                   data-testid="k2-input-category" style={inputStyle} />
                        </Field>
                        <Field label="Kind" testid="kind">
                            <select value={draft.kind || "event"} onChange={e => handleField("kind", e.target.value)}
                                    data-testid="k2-input-kind" style={inputStyle}>
                                <option value="event">event</option>
                                <option value="experience">experience</option>
                            </select>
                        </Field>
                        <Field label="Country (ISO-2)" testid="country">
                            <input value={draft.country || ""} onChange={e => handleField("country", e.target.value.toUpperCase())}
                                   maxLength={3} data-testid="k2-input-country" style={inputStyle} />
                        </Field>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Field label="City" testid="city">
                            <input value={draft.city || ""} onChange={e => handleField("city", e.target.value)}
                                   data-testid="k2-input-city" style={inputStyle} />
                        </Field>
                        <Field label="Venue name" testid="venue_name">
                            <input value={draft.venue_name || ""} onChange={e => handleField("venue_name", e.target.value)}
                                   data-testid="k2-input-venue-name" style={inputStyle} />
                        </Field>
                    </div>
                    <Field label="Concierge whisper (member-facing · ≤ 280 chars)" testid="whisper">
                        <textarea value={draft.concierge_whisper || ""} maxLength={280}
                                  onChange={e => handleField("concierge_whisper", e.target.value)}
                                  data-testid="k2-input-whisper"
                                  style={{ ...inputStyle, minHeight: 80, fontFamily: "inherit" }} />
                        <div style={{ fontSize: 11, color: "#999", textAlign: "right" }}>
                            {(draft.concierge_whisper || "").length}/280
                        </div>
                    </Field>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Field label="Season" testid="season_category">
                            <input value={draft.season_category || ""} onChange={e => handleField("season_category", e.target.value)}
                                   data-testid="k2-input-season" style={inputStyle} />
                        </Field>
                        <Field label="Price from" testid="price_from">
                            <input type="number" value={draft.price_from ?? ""}
                                   onChange={e => handleField("price_from", e.target.value ? parseFloat(e.target.value) : null)}
                                   data-testid="k2-input-price-from" style={inputStyle} />
                        </Field>
                        <Field label="Currency" testid="currency">
                            <input value={draft.currency || ""} maxLength={4}
                                   onChange={e => handleField("currency", e.target.value.toUpperCase())}
                                   data-testid="k2-input-currency" style={inputStyle} />
                        </Field>
                    </div>
                    <Field label="Direct book URL" testid="direct_book_url">
                        <input value={draft.direct_book_url || ""} onChange={e => handleField("direct_book_url", e.target.value)}
                               data-testid="k2-input-direct-book-url" style={inputStyle} />
                    </Field>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12 }}
                           data-testid="k2-field-force_concierge">
                        <input type="checkbox" checked={!!draft.force_concierge}
                               onChange={e => handleField("force_concierge", e.target.checked)}
                               data-testid="k2-input-force-concierge" />
                        <span style={{ fontSize: 13 }}>Force routing through Concierge® desk</span>
                    </label>

                    <div style={{ display: "flex", gap: 8, marginTop: 16, flexWrap: "wrap" }}>
                        <button onClick={save} disabled={!dirty || busy}
                                data-testid="k2-save-btn"
                                style={{
                                    background: dirty ? "#111827" : "#9ca3af",
                                    color: "white", border: 0,
                                    padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                    fontWeight: 600, cursor: dirty ? "pointer" : "default",
                                }}>
                            {busy ? "Saving…" : "Save changes"}
                        </button>
                        {row.publish_status !== "live" ? (
                            <button onClick={() => callAction("publish", "Publish")}
                                    disabled={busy}
                                    data-testid="k2-publish-btn"
                                    style={{
                                        background: "white", color: "#065f46",
                                        border: "1px solid #065f46",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        fontWeight: 600, cursor: "pointer",
                                    }}>
                                Publish
                            </button>
                        ) : null}
                        {row.publish_status === "archived" ? (
                            <button onClick={() => callAction("restore", "Restore")}
                                    disabled={busy}
                                    data-testid="k2-restore-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>
                                Restore to draft
                            </button>
                        ) : (
                            <button onClick={() => callAction("archive", "Archive")}
                                    disabled={busy}
                                    data-testid="k2-archive-btn"
                                    style={{
                                        background: "white", color: "#374151",
                                        border: "1px solid #d1d5db",
                                        padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                        cursor: "pointer",
                                    }}>
                                Archive
                            </button>
                        )}
                        <button onClick={() => callAction("clone", "Clone")}
                                disabled={busy || row.publish_status === "archived"}
                                data-testid="k2-clone-btn"
                                style={{
                                    background: "white", color: "#374151",
                                    border: "1px solid #d1d5db",
                                    padding: "8px 14px", borderRadius: 4, fontSize: 13,
                                    cursor: row.publish_status === "archived" ? "default" : "pointer",
                                    opacity: row.publish_status === "archived" ? 0.5 : 1,
                                }}>
                            Clone
                        </button>
                    </div>
                </div>

                {/* RIGHT · image + audit + provenance */}
                <div data-testid="k2-side">
                    <ImageUploader
                        imageUrl={row.image_url}
                        publicId={row.image_public_id}
                        onUpload={handleImageUpload}
                        onRemove={handleImageRemove}
                        testIdPrefix="k2"
                    />
                    {/* Provenance · internal only · K2 keeps supplier data here */}
                    <div data-testid="k2-provenance" style={{
                        marginTop: 16, padding: 10, fontSize: 11,
                        background: "#f9fafb", color: "#6b7280",
                        borderRadius: 4, lineHeight: 1.5,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 4, color: "#374151" }}>
                            Provenance · internal only
                        </div>
                        <div>id · <code>{row.id}</code></div>
                        <div>cloned_from · <code>{row.cloned_from || "—"}</code></div>
                        <div>provider_ref · <code>{row.provider_ref ? JSON.stringify(row.provider_ref).slice(0, 50) : "—"}</code></div>
                        <div>human_confirmed · {String(row.human_confirmed)}</div>
                        <div>legacy status · {row.status || "—"}</div>
                    </div>

                    {/* Audit trail · K-series single mouth */}
                    <div data-testid="k2-audit" style={{ marginTop: 16 }}>
                        <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13, color: "#374151" }}>
                            Audit trail
                        </div>
                        <div style={{
                            border: "1px solid #e5e7eb", borderRadius: 4,
                            maxHeight: 240, overflow: "auto",
                        }}>
                            {audit.length ? audit.map((a, i) => (
                                <div key={a.id || i}
                                     data-testid={`k2-audit-row-${a.event}`}
                                     style={{
                                         padding: "6px 10px",
                                         borderBottom: i < audit.length - 1 ? "1px solid #f3f4f6" : "none",
                                         fontSize: 11, color: "#374151",
                                     }}>
                                    <div style={{ display: "flex", gap: 8 }}>
                                        <span style={{ fontWeight: 600 }}>{a.event}</span>
                                        <span style={{ color: "#9ca3af" }}>{a.actor}</span>
                                    </div>
                                    <div style={{ fontSize: 10, color: "#9ca3af" }}>{a.at}</div>
                                </div>
                            )) : (
                                <div style={{ padding: 10, color: "#9ca3af", fontSize: 12 }}>
                                    No audit rows yet.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}


export default function EnjoyOverlayCRUD() {
    const [view, setView] = useState("list");
    const [selectedId, setSelectedId] = useState(null);
    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [loading, setLoading] = useState(false);
    const [flagDisabled, setFlagDisabled] = useState(false);

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: 200 };
            if (statusFilter) params.publish_status = statusFilter;
            if (q.trim()) params.q = q.trim();
            const r = await api.get(ROOT, { params });
            setItems(r.data.items || []);
            setTotal(r.data.total || 0);
            setFlagDisabled(false);
        } catch (e) {
            if (e?.response?.status === 503
                && e?.response?.data?.detail?.error === "catalogue_k2_disabled") {
                setFlagDisabled(true);
                setItems([]); setTotal(0);
            } else {
                console.error("K2 list failed:", e);
            }
        } finally { setLoading(false); }
    }, [q, statusFilter]);

    useEffect(() => { refresh(); }, [refresh]);

    if (view === "create") {
        return <div className="os-light-surface"><CreateForm
            onCreated={(id) => { setSelectedId(id); setView("detail"); refresh(); }}
            onCancel={() => setView("list")}
        /></div>;
    }
    if (view === "detail" && selectedId) {
        return <div className="os-light-surface"><DetailView
            rowId={selectedId}
            onBack={() => { setView("list"); refresh(); }}
            onChanged={refresh}
        /></div>;
    }
    return (
        <div className="os-light-surface"><ListView
            items={items} total={total}
            q={q} onSearchChange={setQ}
            statusFilter={statusFilter} onStatusChange={setStatusFilter}
            onOpen={(id) => { setSelectedId(id); setView("detail"); }}
            onCreate={() => setView("create")}
            loading={loading} flagDisabled={flagDisabled}
        /></div>
    );
}
